import { ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup } from '@angular/forms';
import { DrrCcdDeviceControlsService } from './drr-ccd-device-units.controls.service';
import { NetzTranslateService } from 'src/app/services/netz-translate.service';
import { addRemoveAnimation } from 'src/app/+shared/animations/addRemove.animation';
import { CommonService } from 'src/app/services/common.service';
import { DrrCcdTypes, DeviceUnitFormModel, LookupKeys } from '../technical-data.domain';
import { LookupOptionsData } from 'src/app/jurisdiction/jurisdiction.domain';
import { SectionIdentifier } from 'src/app/+shared/components/review-section/review-section.domain';
import { Translations } from './drr-ccd-device-units.models';
import { AllDropdownKeys } from 'src/app/jurisdiction/jurisdiction.constants';
import { merge, Observable, of, Subject } from 'rxjs';

@Component({
  selector: 'app-drr-ccd-device-units',
  templateUrl: './drr-ccd-device-units.component.html',
  styleUrls: ['./drr-ccd-device-units.component.scss'],
  animations: [addRemoveAnimation],
  providers: [DrrCcdDeviceControlsService],
})
export class DrrCcdDeviceUnitsComponent implements OnInit {
  form: FormGroup;
  translations: Translations;

  loadManagementTypeOptions: LookupOptionsData[];
  couplingTypeOptions: LookupOptionsData[];
  measuringConceptOptions: LookupOptionsData[];
  singleDeviceTypeOptions: LookupOptionsData[];
  mainsInterferenceDeviceOptions: LookupOptionsData[];
  connectionTypeOptions: LookupOptionsData[];
  startupTypeOptions: LookupOptionsData[];
  startupCircuitTypeOptions: LookupOptionsData[];
  powerConverterTypeOptions: LookupOptionsData[];
  centerPointCircuitTypeOptions: LookupOptionsData[];
  SectionIdentifier = SectionIdentifier;
  isUnitEditable: boolean = false;

  private clearFileUploads$ = new Subject<boolean>();
  private clearStreams = new WeakMap<AbstractControl, Observable<boolean>>();

  private _cddType: string;
  get ccdType(): string {
    return this._cddType;
  }

  @Input() set ccdType(value: string) {
    this._cddType = value;
    this.controlsService.ccdType = value;
    if (this.form) {
      for (const unitForm of this.units.controls) {
        this.controlsService.toggleAllControls(this.controlsService.unitControls, unitForm as FormGroup, null);
        this.controlsService.resetUnitControls(this.controlsService.unitControls, unitForm as FormGroup);
      }
      this.clearFileUploads$.next(true);
      this.addDefaultUnit();
      this.enableUnitControls();
    }
    this.setTranslations();
  }
  @Input() data: DeviceUnitFormModel[];
  @Input() applicationId: any;
  @Input() isDetailsView: boolean;
  @Input() allowAddUnit: boolean = true;
  @Input() allowAddSubUnit: boolean = true;
  @Input() isRegistrationDevice: boolean = false;
  @Input() deviceIndex: number = 0;
  @Input() isDeviceEditable: boolean = true;

  @Output() formInited = new EventEmitter<FormGroup>();

  constructor(
    private fb: FormBuilder,
    private cd: ChangeDetectorRef,
    private translation: NetzTranslateService,
    private commonService: CommonService,
    public controlsService: DrrCcdDeviceControlsService
  ) {
    this.setTranslations();
  }

  ngOnInit(): void {
    this.form = this.fb.group({
      units: this.fb.array([]),
    });
    this.patchFormData();
    this.formInited.emit(this.form);
    this.loadLookups();

    this.translation.registerForLanguageChange().subscribe(_ => {
      this.loadLookups();
      this.setTranslations();
    });
  }

  private patchFormData() {
    if (this.data) {
      this.data.forEach(unit => {
        const group = this.controlsService.addUnit(unit);
        (this.form.controls.units as FormArray).push(group);
      });
    }
  }

  private loadLookups() {
    this.loadManagementTypeOptions = this.commonService.getDropdownData(LookupKeys.LoadManagementType);
    this.couplingTypeOptions = this.commonService.getDropdownData(LookupKeys.CouplingType);
    this.measuringConceptOptions = this.commonService.getDropdownData(AllDropdownKeys.FeedInMeasuringConcept);
    this.singleDeviceTypeOptions = this.commonService.getDropdownData(LookupKeys.SingleDeviceType);
    this.mainsInterferenceDeviceOptions = this.commonService.getDropdownData(LookupKeys.MainsInterferenceDeviceType);
    this.connectionTypeOptions = this.commonService.getDropdownData(LookupKeys.ConnectionType);
    this.startupTypeOptions = this.commonService.getDropdownData(LookupKeys.StartUpType);
    this.startupCircuitTypeOptions = this.commonService.getDropdownData(LookupKeys.StartUpCircuit);
    this.powerConverterTypeOptions = this.commonService.getDropdownData(LookupKeys.PowerConverterType);
    this.centerPointCircuitTypeOptions = this.commonService.getDropdownData(LookupKeys.CenterPointCircuitType);
  }

  private addDefaultUnit() {
    if (!this.data && this.units.controls.length === 0) {
      const group = this.controlsService.addUnit(null);
      (this.form.controls.units as FormArray).push(group);
    }
  }

  get units(): FormArray {
    return this.form.controls.units as FormArray;
  }

  get showUploadUnitDataSheet(): boolean {
    return [
      DrrCcdTypes.heatPump,
      DrrCcdTypes.airConditioner,
      DrrCcdTypes.wallBox,
      DrrCcdTypes.chargingDevice,
      DrrCcdTypes.storage,
    ].includes(this.ccdType);
  }

  // stable reference per unit so the child subscribes only once
  fileClearStream(unit: FormGroup): Observable<boolean> {
    if (!this.clearStreams.has(unit)) {
      this.clearStreams.set(unit, merge(unit.controls.unavailable.valueChanges as Observable<boolean>, this.clearFileUploads$));
    }
    return this.clearStreams.get(unit) ?? of(false);
  }

  showAdditionalHeaterField(index: number): boolean {
    if ([DrrCcdTypes.heatPump, DrrCcdTypes.airConditioner].includes(this.ccdType)) {
      return this.units.controls[index].get('hasAdditionalHeating')?.value;
    }
    return false;
  }

  showAdditionalHeaterFieldSubUnit(unit: FormGroup, index: number): boolean {
    const subUnits = unit.controls.subUnits as FormArray;
    if ([DrrCcdTypes.heatPump, DrrCcdTypes.airConditioner].includes(this.ccdType)) {
      return subUnits.controls[index].get('hasAdditionalHeating')?.value;
    }
    return false;
  }

  showLoadManagementType(index: number): boolean {
    if ([DrrCcdTypes.wallBox, DrrCcdTypes.chargingDevice].includes(this.ccdType)) {
      return this.units.controls[index].get('loadManagementAvailable')?.value;
    }
    return false;
  }

  get showUploadCircuitDiagram(): boolean {
    return [DrrCcdTypes.storage].includes(this.ccdType);
  }

  get showUploadDataCollection(): boolean {
    return [
      DrrCcdTypes.heating,
      DrrCcdTypes.singleDevice,
      DrrCcdTypes.energyManagement,
      DrrCcdTypes.emergencyGenerator,
      DrrCcdTypes.mainsInterference,
    ].includes(this.ccdType);
  }

  get showUploadSitePlanInstallation(): boolean {
    return [DrrCcdTypes.heating, DrrCcdTypes.singleDevice, DrrCcdTypes.energyManagement, DrrCcdTypes.emergencyGenerator].includes(
      this.ccdType
    );
  }

  get showParallelOperationNote(): boolean {
    return this.isRegistrationDevice && [DrrCcdTypes.storage].includes(this.ccdType);
  }

  showUploadDataSheetWr(index: number): boolean {
    return [DrrCcdTypes.storage].includes(this.ccdType) && this.units.controls[index].get('couplingType').value === 'ac';
  }

  get unitAddLabel(): string {
    let label: string;
    switch (this.ccdType) {
      case DrrCcdTypes.storage:
        label = 'drr-ccd-devices.add-storage-btn';
        break;
      case DrrCcdTypes.wallBox:
      case DrrCcdTypes.chargingDevice:
        label = 'drr-ccd-devices.add-wallbox-btn';
        break;
      case DrrCcdTypes.heatPump:
        label = 'drr-ccd-devices.add-inner-unit-btn';
        break;
      case DrrCcdTypes.airConditioner:
        label = 'drr-ccd-devices.add-outer-unit-btn';
        break;
      case DrrCcdTypes.heating:
        label = 'drr-ccd-devices.add-heating-btn';
        break;
      case DrrCcdTypes.singleDevice:
        label = 'drr-ccd-devices.add-singel-device-btn';
        break;
      case DrrCcdTypes.energyManagement:
        label = 'drr-ccd-devices.add-energy-management-btn';
        break;
      case DrrCcdTypes.emergencyGenerator:
        label = 'drr-ccd-devices.add-emergency-generator-btn';
        break;
      default:
        label = 'drr-ccd-devices.add-btn';
    }
    return this.translation.translateString(label);
  }

  getSubUnitLabel(num: number): string {
    let label = 'drr-ccd-devices.add-sub-btn';
    if (this.ccdType === DrrCcdTypes.heatPump) {
      label = 'drr-ccd-devices.add-sub-outer-unit-to-btn';
    } else if (this.ccdType === DrrCcdTypes.airConditioner) {
      label = 'drr-ccd-devices.add-sub-inner-unit-to-btn';
    }
    return this.translation.translateString(label, { innerUnit: num });
  }

  addUnit() {
    const group = this.controlsService.addUnit(null);
    (this.form.controls.units as FormArray).push(group);
  }

  fileFormInited(file: FormGroup, parentForm: FormGroup, controlName: string) {
    parentForm.addControl(controlName, file);
    parentForm.updateValueAndValidity();
    this.cd.detectChanges();
  }

  removeUnit(index: number) {
    (this.form.controls.units as FormArray).removeAt(index);
    this.form.updateValueAndValidity();
  }

  removeSubUnit(unit: FormGroup, index: number) {
    const subUnits = unit.controls.subUnits as FormArray;
    subUnits.removeAt(index);
    this.controlsService.setDisabledForSubUnits(unit.controls.unavailable.value, subUnits);
    this.form.updateValueAndValidity();
  }

  private setTranslations() {
    const translations: Translations = new Translations();

    switch (this.ccdType) {
      case DrrCcdTypes.heatPump:
        translations.unitHeader = this.translation.translateString('drr-ccd-devices.inner-unit-title');
        translations.subUnitHeader = this.translation.translateString('drr-ccd-devices.outer-unit-title');
        break;
      case DrrCcdTypes.airConditioner:
        translations.unitHeader = this.translation.translateString('drr-ccd-devices.outer-unit-title');
        translations.subUnitHeader = this.translation.translateString('drr-ccd-devices.inner-unit-title');
        break;
      case DrrCcdTypes.wallBox:
      case DrrCcdTypes.chargingDevice:
        translations.unitHeader = this.translation.translateString('drr-ccd-devices.charging-infrastructure-title');
        break;
      case DrrCcdTypes.storage:
        translations.unitHeader = this.translation.translateString('drr-ccd-devices.storage-title');
        break;
      case DrrCcdTypes.heating:
        translations.unitHeader = this.translation.translateString('drr-ccd-devices.heating-title');
        break;
      case DrrCcdTypes.singleDevice:
        translations.unitHeader = this.translation.translateString('drr-ccd-devices.single-device-title');
        break;
      case DrrCcdTypes.energyManagement:
        translations.unitHeader = this.translation.translateString('drr-ccd-devices.energy-management-title');
        break;
      case DrrCcdTypes.emergencyGenerator:
        translations.unitHeader = this.translation.translateString('drr-ccd-devices.emergency-generator-title');
        break;
      case DrrCcdTypes.mainsInterference:
        translations.unitHeader = this.translation.translateString('drr-ccd-devices.mains-interference-title');
        break;
      case DrrCcdTypes.other:
        translations.unitHeader = this.translation.translateString('drr-ccd-devices.other-title');
        break;
    }

    this.translations = translations;
  }

  private enableUnitControls() {
    if (this.isDetailsView && this.isDeviceEditable) {
      this.isUnitEditable = true;
    }
  }
}
